import React, { FunctionComponent, useEffect, useState } from 'react';
import domPurify from 'dompurify';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';
import { joinStrings } from '../../utils/string';
import { Heading } from '../shared/Heading';
import { Button } from '../shared/Button';
import { PopUpHandler } from '../shared/PopupHandler';
import { Paragraph } from '../shared/Paragraph';
import './styles';
import { getPromoCodeParam } from '../../hooks/registration/useRegistrationStepper.helper';
import { useFetchPromoDetails } from '../../hooks/useFetchPromoDetails';
import { locale } from '../../consts';
import { BonusGetBySlugQuery } from '../../libs/graphql/betzoneDirectusAPI/queries/__generated__/bonus-get-by-slug.query.generated';
import { getImageUrl } from '../../utils/navigation';

type WelcomeBonusBannerDetails = {
  termsContent?: string;
  title?: string;
  description?: string;
  imageMobile?: string;
  imageDesktop?: string;
};

export const WelcomeBonusBanner: FunctionComponent = () => {
  const { isMobileDevice } = useDeviceInfo();
  const sanitizer = domPurify.sanitize;

  const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false);
  const [bonus, setBonus] = useState<WelcomeBonusBannerDetails | null>(null);
  const { promoValue } = getPromoCodeParam();

  const processPromo = (bonus: BonusGetBySlugQuery): void => {
    console.log('promo', bonus);
    setBonus({
      termsContent: bonus?.bonus?.[0]?.translations?.[0]?.terms_and_conditions,
      title: bonus?.bonus?.[0]?.translations?.[0]?.title,
      description: bonus?.bonus?.[0]?.translations?.[0]?.description,
      imageMobile: getImageUrl(bonus?.bonus?.[0]?.translations?.[0]?.registration_image_mobile?.filename_disk),
      imageDesktop: getImageUrl(bonus?.bonus?.[0]?.translations?.[0]?.registration_image_desktop?.filename_disk),
    } as WelcomeBonusBannerDetails);
  };

  const { fetchBonusDetailsBySlug } = useFetchPromoDetails({
    language: locale,
    slug: promoValue || 'defaultwelcomepromo',
    onFetchBonusBySlugComplete: processPromo,
  });

  useEffect(() => {
    fetchBonusDetailsBySlug();
  }, []);

  return (
    <>
      <div
        style={{ backgroundImage: isMobileDevice ? `url(${bonus?.imageMobile})` : `url(${bonus?.imageDesktop})` }}
        className={joinStrings([
          'welcome-bonus-banner',
          isMobileDevice && 'welcome-bonus-banner--mobile',
          isMobileDevice &&
            ((bonus?.description?.length || 0) > 700
              ? 'welcome-bonus-banner--mobile-expanded'
              : 'welcome-bonus-banner--mobile-compact'),
        ])}
      >
        <div
          className={joinStrings([
            'welcome-bonus-banner__title-wrapper',
            isMobileDevice && 'welcome-bonus-banner__title-wrapper--mobile',
          ])}
        >
          <Heading level={3} styleAs={3} className="welcome-bonus-banner__title">
            {bonus?.title || ''}
          </Heading>
          <Paragraph className="welcome-bonus-banner__description">{bonus?.description}</Paragraph>
        </div>
        {bonus?.termsContent && (
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={(): void => {
              setIsPopupVisible(true);
            }}
          >
            {isMobileDevice ? 'Terms' : 'Full terms'}
          </Button>
        )}
      </div>
      {isPopupVisible && (
        <PopUpHandler
          isOpen
          onClose={(): void => {
            setIsPopupVisible(false);
          }}
          header="Welcome Bonus Details"
          modalProps={{
            className: 'welcome-bonus-banner__modal',
            'aria-label': 'privacy policy heading',
          }}
        >
          <Paragraph dangerouslySetInnerHTML={{ __html: sanitizer(bonus?.termsContent || '') }} />
          <Button
            type="button"
            size="x-large"
            className="terms-back-btn"
            onClick={(): void => {
              setIsPopupVisible(false);
            }}
          >
            Continue Registration
          </Button>
        </PopUpHandler>
      )}
    </>
  );
};
